import * as Icons from "@material-ui/icons";
import { unparse as convertToCSV } from "papaparse/papaparse.min";
import React from "react";
import {
  ArrayInput,
  BooleanField,
  BooleanInput,
  CardActions,
  Create,
  CreateButton,
  Datagrid,
  DateField,
  DateInput,
  downloadCSV,
  Edit,
  ExportButton,
  Filter,
  FormTab,
  FunctionField,
  ImageField,
  List,
  NumberInput,
  ReferenceArrayField,
  ReferenceField,
  ReferenceInput,
  RefreshButton,
  SelectInput,
  ShowController,
  SimpleFormIterator,
  TabbedForm,
  TextField,
  TextInput,
  UrlField,
  ArrayField
} from "react-admin";
import SchoolEditActions from "./../actions/schoolEditActions";

const sourceOfRegistrationOptions = [
  { id: "obby", name: "Obby" },
  { id: "baluu", name: "Baluu" }
];

const SchoolStatus = {
  ACCOUNTCREATED: "accountcreated",
  WAITINGFORAPPROVAL: "waitingforapproval",
  ENABLED: "enabled",
  ARCHIVED: "archived",
  REMOVED: "removed"
};

// Helper component to manage school add-ons inline
function SchoolAddonsTab({ record = {} }) {
  const schoolId = record._id || record.id;
  if (!schoolId) return <p>Save the school first to manage add-ons.</p>;

  const apiUrl = process.env.REACT_APP_API_URL || "";
  const manageUrl = `${window.location.origin}/#/schoolAddons?filter=${encodeURIComponent(JSON.stringify({ schoolID: schoolId }))}`;
  const createUrl = `${window.location.origin}/#/schoolAddons/create?source=${encodeURIComponent(JSON.stringify({ schoolID: schoolId }))}`;

  return (
    <div style={{ padding: "16px 0" }}>
      <div style={{
        padding: "16px",
        background: "#e3f2fd",
        borderRadius: "4px",
        border: "1px solid #90caf9",
        marginBottom: "16px"
      }}>
        <h3 style={{ margin: "0 0 8px 0", color: "#1565c0" }}>School Add-ons</h3>
        <p style={{ margin: "0 0 12px 0", fontSize: "13px", color: "#444" }}>
          Manage subscription add-ons for this school. Add-ons can be enabled for free
          or with a Stripe subscription charge.
        </p>
        <div style={{ display: "flex", gap: "8px" }}>
          <a
            href={manageUrl}
            style={{
              background: "#1976d2",
              color: "white",
              padding: "8px 16px",
              borderRadius: "4px",
              textDecoration: "none",
              fontSize: "13px"
            }}
          >
            View School Add-ons
          </a>
          <a
            href={createUrl}
            style={{
              background: "#4caf50",
              color: "white",
              padding: "8px 16px",
              borderRadius: "4px",
              textDecoration: "none",
              fontSize: "13px"
            }}
          >
            + Assign New Add-on
          </a>
        </div>
      </div>
      <div style={{
        padding: "12px",
        background: "#fff3e0",
        borderRadius: "4px",
        border: "1px solid #ffcc80",
        fontSize: "12px",
        color: "#e65100"
      }}>
        <strong>Available Add-on Slugs:</strong>
        <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
          <li><code>custom-email-domain</code> - Custom Email Domain</li>
          <li><code>custom-domain-website</code> - Custom Domain Website</li>
          <li><code>custom-pages-work</code> - Custom Pages (work)</li>
        </ul>
        <p style={{ margin: "8px 0 0 0" }}>
          Enabling "custom-domain-website" or "custom-email-domain" will auto-set the
          corresponding flags in the Custom Domain tab.
        </p>
      </div>
    </div>
  );
}

// Helper component to show plan tier info
function PlanTierInfo() {
  return (
    <div style={{
      marginTop: "8px",
      padding: "12px",
      background: "#f5f5f5",
      borderRadius: "4px",
      fontSize: "13px"
    }}>
      <strong>Plan Tiers:</strong>
      <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
        <li><strong>Standard</strong> - 3% + 50p (unlimited transactions)</li>
        <li><strong>Lite</strong> - 2.5% + 40p (150 transactions/month)</li>
        <li><strong>Growth</strong> - 2% + 30p (500 transactions/month)</li>
        <li><strong>Scale</strong> - 1% + 20p (1500 transactions/month)</li>
      </ul>
      <p style={{ margin: "8px 0 0 0", color: "#666" }}>
        <em>Note: Custom Commission overrides tier rates if set above.</em>
      </p>
    </div>
  );
}

const exporter = schools => {
  let defaultObjAccountManagement = {
    totalGMV: 0,
    totalBookings: 0,
    last3monthsGMV: 0,
    totalClasses: 0,
    totalLiveClasses: 0,
    totalLiveSessions: 0,
    daysToInactivity: -1,
    widgetMSPEnabled: false,
    totalReviews: 0,
    totalBookingsOfWidget: 0,
    haveInsurance: false,
    wantInsurance: false,
    giftCardEnabled: false,
    flexitimeEnabled: false
  };
  const schoolsForExport = schools.map(school => {
    let schoolForExport = {};
    if (typeof school.accountManagment == "undefined") {
      school.accountManagment = defaultObjAccountManagement;
    }
    schoolForExport.id = school.id;
    schoolForExport.name = school.name;
    schoolForExport.email = school.email;
    schoolForExport.ca = school.ca;
    schoolForExport.bookingCommission = school.bookingCommission;
    schoolForExport.rating = school.rating;
    schoolForExport.createdAt = school.createdAt;
    schoolForExport.totalGMV = school.accountManagment.totalGMV;
    schoolForExport.totalBookings = school.accountManagment.totalBookings;
    schoolForExport.last3monthsGMV = school.accountManagment.last3monthsGMV;
    schoolForExport.totalClasses = school.accountManagment.totalClasses;
    schoolForExport.totalLiveClasses = school.accountManagment.totalLiveClasses;
    schoolForExport.totalLiveSessions =
      school.accountManagment.totalLiveSessions;
    schoolForExport.daysToInactivity = school.accountManagment.daysToInactivity;
    schoolForExport.widgetMSPEnabled = school.accountManagment.widgetMSPEnabled;
    schoolForExport.totalReviews = school.accountManagment.totalReviews;
    schoolForExport.totalBookingsOfWidget =
      school.accountManagment.totalBookingsOfWidget;
    schoolForExport.haveInsurance = school.accountManagment.haveInsurance;
    schoolForExport.wantInsurance = school.accountManagment.wantInsurance;
    schoolForExport.giftCardEnabled = school.accountManagment.giftCardEnabled;
    schoolForExport.flexitimeEnabled = school.accountManagment.flexitimeEnabled;
    return schoolForExport;
  });
  const csv = convertToCSV({
    data: schoolsForExport,
    fields: [
      "id",
      "name",
      "email",
      "ca",
      "bookingCommission",
      "rating",
      "createdAt",
      "totalGMV",
      "totalBookings",
      "last3monthsGMV",
      "totalClasses",
      "totalLiveClasses",
      "totalLiveSessions",
      "daysToInactivity",
      "widgetMSPEnabled",
      "totalReviews",
      "totalBookingsOfWidget",
      "haveInsurance",
      "wantInsurance",
      "giftCardEnabled",
      "flexitimeEnabled"
    ] // order fields in the export
  });
  downloadCSV(csv, "schools"); // download as 'schools.csv` file
};

const PostActions = ({
  bulkActions,
  basePath,
  currentSort,
  displayedFilters,
  exporter,
  filters,
  filterValues,
  onUnselectItems,
  resource,
  selectedIds,
  showFilter,
  total
}) => (
  <CardActions>
    {bulkActions &&
      React.cloneElement(bulkActions, {
        basePath,
        filterValues,
        resource,
        selectedIds,
        onUnselectItems
      })}
    {filters &&
      React.cloneElement(filters, {
        resource,
        showFilter,
        displayedFilters,
        filterValues,
        context: "button"
      })}
    <CreateButton basePath={basePath} />
    <ExportButton
      disabled={total === 0}
      resource={resource}
      sort={currentSort}
      filter={filterValues}
      exporter={exporter}
      maxResults={10000}
    />
    <RefreshButton />
  </CardActions>
);

const SchoolFilter = props => (
  <Filter {...props}>
    <TextInput label="Search by ID" source="qID" />
    <TextInput label="Search by name" source="qName" alwaysOn />
    <TextInput label="Search by school email" source="qEmail" />
    <SelectInput
      label="Searcy By School Status"
      source="qStatus"
      choices={[
        {
          id: SchoolStatus.ACCOUNTCREATED,
          name: SchoolStatus.ACCOUNTCREATED
        },
        {
          id: SchoolStatus.WAITINGFORAPPROVAL,
          name: SchoolStatus.WAITINGFORAPPROVAL
        },
        { id: SchoolStatus.ENABLED, name: SchoolStatus.ENABLED },
        { id: SchoolStatus.ARCHIVED, name: SchoolStatus.ARCHIVED },
        { id: SchoolStatus.REMOVED, name: SchoolStatus.REMOVED }
      ]}
    />
    <SelectInput
      label="Searcy By Account type"
      source="qPlanID"
      choices={[
        {
          id: "ePwQWNDEemSas5d5K",
          name: "Enterprise"
        },
        {
          id: "6c25bmFXdAyCJG2sY",
          name: "Growth"
        },
        {
          id: "jBndSmWp4pTnShE5n",
          name: "Standard"
        },
        {
          id: "6QBhPfCCPESSKz6pn",
          name: "Lite"
        },
        {
          id: "DdTieaq3XHBPSaWuz",
          name: "Free"
        },
        {
          id: "egZnPaYnqevKkjGhC",
          name: "Legacy Widget"
        },
        {
          id: "aNr28vAiukHBMKLkW",
          name: "Legacy Marketplace"
        }
      ]}
    />
    <SelectInput
      label="Filter by Source of Registration"
      source="qSourceOfRegistration"
      choices={sourceOfRegistrationOptions}
    />
    <SelectInput
      label="Filter by Obby Validated"
      source="qObbyValidate"
      choices={[
        { id: "true", name: "Validated" },
        { id: "false", name: "Not Validated" }
      ]}
    />
    <SelectInput
      label="Filter by Account Validated"
      source="qAccountValidated"
      choices={[
        { id: "true", name: "Validated" },
        { id: "false", name: "Not Validated" }
      ]}
    />
  </Filter>
);

export const SchoolList = props => (
  <List
    {...props}
    filters={<SchoolFilter />}
    sort={{ field: "createdAt", order: "DESC" }}
    actions={<PostActions />}
    exporter={exporter}
  >
    <Datagrid rowClick="edit">
      <TextField source="id" />
      <TextField source="name" />
      <TextField source="slug" />
      <TextField source="status" />
      <TextField label="Stripe Account Type" source="stripeConnect.type" />
      <FunctionField
        source="sourceOfRegistration"
        render={item =>
          item.sourceOfRegistration === "baluu" ? "Baluu" : "Obby"
        }
      />
      <BooleanField label="Obby Validated" source="obbyValidate" />
      <BooleanField label="Account Validated" source="accountValidated" />
      <DateField source="createdAt" />
    </Datagrid>
  </List>
);

export const SchoolCreate = props => (
  <Create {...props}>
    <TabbedForm>
      <FormTab label="summary">
        <BooleanInput label="Commercial agreement" source="ca" />
        <BooleanInput
          label="Hide from Google"
          source="hidden"
        />
        <TextInput label="Name" source="name" />
        <TextInput disabled label="Slug" source="slug" />
        <TextInput
          multiline
          fullWidth
          label="Description"
          source="description"
        />
        <TextInput label="Email" source="email" type="email" />
        <TextInput label="Website" source="website" />
        <TextInput label="Phone" source="phone" />
        <TextInput label="VAT Number" source="vatNumber" />
        <TextInput disabled label="Hash" source="emailHash" />
        <BooleanInput label="Hide course" source="hideCourses" />
        <BooleanInput label="Msp" source="msp" />
        <DateInput label="Created at" source="createdAt" />
        <DateInput label="Updated at" source="updatedAt" />
        <DateInput label="Trial End Date" source="trialDetails.trialEnds" />
        <TextInput source="groupName" />
        <TextInput label="Partner original URL" source="website" />
        <TextInput
          label="Account manager"
          source="accountManager"
          type="email"
        />
        <SelectInput
          label="Source of Registration"
          source="sourceOfRegistration"
          choices={sourceOfRegistrationOptions}
        />
      </FormTab>
      <FormTab label="Commission">
        <NumberInput
          label="Marketplace Commission"
          source="bookingCommission"
        />
        <NumberInput label="Widget Commission" source="widgetCommission" />
        <NumberInput label="Online Commission" source="onlineCommission" />
        <NumberInput label="Rating" source="rating" />
        <NumberInput label="Response rate" source="responseRate" />
        <TextInput label="Response time" source="responseTime" />
      </FormTab>

      <FormTab label="Images">
        <ArrayInput source="images">
          <SimpleFormIterator>
            <TextInput source="url" />
            <TextInput source="alt" />
            <TextInput source="title" />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>
    </TabbedForm>
  </Create>
);

export const SchoolEdit = props => (
  <ShowController {...props}>
    {({ record = {}, ...controllerProps }) => {
      return (
        <Edit {...props} actions={<SchoolEditActions />}>
          <TabbedForm>
            <FormTab label="summary">
              <TextInput disabled label="Id" source="id" />
              <SelectInput
                label="Status"
                source="status"
                choices={[
                  {
                    id: SchoolStatus.ACCOUNTCREATED,
                    name: SchoolStatus.ACCOUNTCREATED
                  },
                  {
                    id: SchoolStatus.WAITINGFORAPPROVAL,
                    name: SchoolStatus.WAITINGFORAPPROVAL
                  },
                  { id: SchoolStatus.ENABLED, name: SchoolStatus.ENABLED },
                  { id: SchoolStatus.ARCHIVED, name: SchoolStatus.ARCHIVED },
                  { id: SchoolStatus.REMOVED, name: SchoolStatus.REMOVED }
                ]}
              />
              <SelectInput
                label="Preferred Currency"
                source="preferredCurrency"
                choices={[
                  { id: "GBP", name: "GBP" },
                  { id: "EUR", name: "EUR" },
                  { id: "USD", name: "USD" },
                  { id: "AUD", name: "AUD" },
                  { id: "NZD", name: "NZD" },
                  { id: "CAD", name: "CAD" }
                ]}
              />

              <BooleanInput
                label="Hide course"
                source="hideCourses"
              />
              <TextInput label="Name" source="name" />
              <TextInput disabled label="Slug" source="slug" />
              <BooleanInput
                label="Is Widget enabled?"
                source="isWidgetEnabled"
              />
              <TextInput
                multiline
                fullWidth
                label="Description"
                source="description"
              />
              <TextInput label="Email" source="email" type="email" />
              <TextInput label="Website" source="website" />
              <TextInput label="Phone" source="phone" />
              <TextInput disabled label="Hash" source="emailHash" />
              <BooleanInput label="Msp" source="msp" />
              <DateInput label="Created at" source="createdAt" />
              <DateInput label="Updated at" source="updatedAt" />
              <DateInput
                label="Trial End Date"
                source="trialDetails.trialEnds"
              />
              <TextInput source="groupName" />
              <TextInput label="Partner original URL" source="website" />
              <ReferenceInput
                label="Account Type"
                source="planId"
                reference="plans"
                sort={{ field: "order", order: "ASC" }}
              >
                <SelectInput optionText="name" />
              </ReferenceInput>
              <SelectInput
                label="Source of Registration"
                source="sourceOfRegistration"
                choices={sourceOfRegistrationOptions}
              />
              <SelectInput
                label="Preferred App"
                source="preferredApp"
                choices={sourceOfRegistrationOptions}
              />
              <TextField
                source="preferredCurrency"
                label="Preferred Currency"
              />
              <TextField source="timezone" label="Timezone" />
            </FormTab>
            <FormTab label="Images">
              <ImageField source="images" src="url" title="title" />
            </FormTab>
            <FormTab label="Subscription&Connect">
              <h3>Marketing Package</h3>
              <BooleanInput label="Youmo" source="marketingPackage.youmo" />

              <h3>Plan Tier (New System)</h3>
              <ReferenceInput
                label="Plan Tier"
                source="planTierId"
                reference="planTiers"
                allowEmpty
              >
                <SelectInput
                  optionText={(record) => record ? `${record.name} (${record.commissionPercent}% + ${record.minimumFeeCents}p)` : 'Default Tier'}
                  emptyText="Default Tier (Standard)"
                />
              </ReferenceInput>
              <BooleanInput
                label="Free Access"
                source="isFreeAccess"
                helperText="Grant access to the selected tier without requiring a subscription."
              />
              <PlanTierInfo />

              <h3>Subscription Data</h3>
              <TextField label="Subscription ID" source="subscription" />
              <TextField
                label="Subscription Status"
                source="subscriptionStatus"
              />
              <BooleanInput
                label="Is Subscription marked as unpaid?"
                source="subscriptionIsUnpaid"
              />
              <h3>Current Connect Data</h3>
              <BooleanInput
                label="Is Stripe Connect Enabled"
                source="stripeConnect.enabled"
              />
              <TextField
                label="Stripe Account ID"
                source="stripeConnect.accountID"
              />
              <TextField
                label="Stripe Account Type"
                source="stripeConnect.type"
              />
              <BooleanInput source="stripeConnect.directChargesEnabled" />
              <BooleanInput source="stripeConnect.stripeHostedPage" />
              <DateField
                source="stripeConnect.stripeFormatedData.stripeAccountCreatedAt"
                label="Start Date"
              />
              <DateField
                source="stripeConnect.stripeDateUpdateAt"
                label="Stripe data update at"
              />
              <h3>Alternative Connect Data</h3>
              <BooleanInput
                label="Is Stripe Connect Enabled"
                source="stripeConnectAlt.enabled"
              />
              <TextField
                label="Stripe Account ID"
                source="stripeConnectAlt.accountID"
              />
              <TextField
                label="Stripe Account Type"
                source="stripeConnectAlt.type"
              />
              <BooleanInput source="stripeConnectAlt.directChargesEnabled" />
              <BooleanInput source="stripeConnectAlt.stripeHostedPage" />
              <DateField
                source="stripeConnectAlt.stripeFormatedData.stripeAccountCreatedAt"
                label="Start Date"
              />
              <DateField
                source="stripeConnectAlt.stripeDateUpdateAt"
                label="Stripe data update at"
              />
              <h3>OLD Connect Data</h3>
              <BooleanInput
                label="Is Stripe Connect Enabled"
                source="stripeConnectOld.enabled"
              />
              <TextField
                label="Stripe Account ID"
                source="stripeConnectOld.accountID"
              />
              <TextField
                label="Stripe Account Type"
                source="stripeConnectOld.type"
              />
              <BooleanInput source="stripeConnectOld.directChargesEnabled" />
              <BooleanInput source="stripeConnectOld.stripeHostedPage" />
              <DateField
                source="stripeConnectOld.stripeFormatedData.stripeAccountCreatedAt"
                label="Start Date"
              />
              <DateField
                source="stripeConnectOld.stripeDateUpdateAt"
                label="Stripe data update at"
              />
            </FormTab>
            <FormTab label="Account Management">
              <BooleanInput
                label="Account Validated (disables 2FA — set true once we trust the school's email, e.g. Stripe connected)"
                source="accountValidated"
              />
              <BooleanInput
                label="Is Stripe Connect Validated By Obby"
                source="obbyValidate"
              />
              <NumberInput label="Custom Commission" source="customComission" />
              <BooleanInput
                label="Refundable comission fee"
                source="refundableComissionFee"
              />
              <TextInput label="Custom Commission Message" source="customComissionMessage" />
              <NumberInput
                label="Custom Minimum Fee (cents/pence)"
                source="customMinimumFeeCents"
                helperText="Leave empty for plan default. Set to 0 for no minimum fee."
              />
              <NumberInput
                label="Custom Monthly Transaction Cap"
                source="customMonthlyTransactionCap"
                helperText="Leave empty for plan default. Set to 0 for unlimited."
              />
            </FormTab>
            <FormTab label="Basket and checkout">
              <h3>Basket</h3>
              <BooleanInput
                label="Basket Enabled"
                source="basket.isEnabled"
              />
              <BooleanInput
                label="Multi-Date Selection"
                source="basket.enableMultiDateSelection"
              />
              <h3>Tickets V2</h3>
              <SelectInput
                label="Tickets V2 Mode"
                source="ticketsV2Mode"
                choices={[
                  { id: "disabled", name: "Disabled (preview only)" },
                  { id: "enabled", name: "Enabled (can activate per listing)" },
                  { id: "v2_only", name: "V2 Only (forced, no V1)" }
                ]}
              />
            </FormTab>

            <FormTab label="Custom Domain">
              <SelectInput
                label="Preferred App"
                source="preferredApp"
                choices={sourceOfRegistrationOptions}
              />
              <hr />
              <BooleanInput
                label="Is custom domain enabled?"
                source="customDomain.isEnabled"
              />
              <BooleanInput
                label="User wants custom domain?"
                source="customDomain.usersWants"
              />
              <BooleanInput
                label="Custom domain is setup?"
                source="customDomain.isSetup"
              />
              <TextInput
                label="Custom domain"
                source="customDomain.userdomain"
              />
              <hr />
              <BooleanInput
                label="Is custom email domain enabled?"
                source="customEmailDomain.isEnabled"
              />
              <BooleanInput
                label="User wants email custom domain?"
                source="customEmailDomain.usersWants"
              />
              <BooleanInput
                label="Email custom domain is setup?"
                source="customEmailDomain.isSetup"
              />
              <TextInput
                label="Custom email domain"
                source="customEmailDomain.userdomain"
              />
            </FormTab>

            <FormTab label="Add-ons">
              <SchoolAddonsTab />
            </FormTab>

            <FormTab label="WhatsApp AI Chatbot">
              <h3>Beta Access</h3>
              <BooleanInput
                label="AI Chatbot Beta Enabled (allows school to access AI Chatbot settings)"
                source="whatsapp.betaEnabled"
              />
              <h3>WhatsApp Configuration</h3>
              <BooleanInput
                label="WhatsApp AI Chatbot Enabled"
                source="whatsapp.enabled"
              />
              <TextField
                label="Twilio Phone Number (auto-provisioned)"
                source="whatsapp.twilioPhoneNumber"
              />
              <TextField
                label="Twilio Phone Number SID"
                source="whatsapp.twilioPhoneNumberSid"
              />
              <h3>AI Messages</h3>
              <TextInput
                multiline
                fullWidth
                label="Welcome Message"
                source="whatsapp.welcomeMessage"
              />
              <TextInput
                multiline
                fullWidth
                label="Custom System Prompt"
                source="whatsapp.systemPrompt"
              />
            </FormTab>
          </TabbedForm>
        </Edit>
      );
    }}
  </ShowController>
);
